import { FC, useMemo } from 'react';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import Button from '../../../../../components/button/index';
import { CloseModalButton } from '../../../../../components/close-modal-button/index';
import { ModalWrapper } from '../../../../../components/modal-wrapper/index';
import { useLocale } from '../../../../../hooks/index';
import {
  getDocumentCategoryDisplayLabel,
  getDocumentSubCategoryDisplayLabel,
} from '../../../constants/document-category.constant';
import {
  expirationPeriodLabelKey,
  SigningMode,
  signingModeLabelKey,
} from '../../../templates/enums/template.const';
import { SignaturePopupType } from '../../enums/signature-popup-type.const';
import { useLinkedTargetOptions } from '../../hooks/use-linked-target-options.hook';
import {
  SignaturePopupStore,
  ViewDocumentDetailsMetadata,
} from '../../store/signature-popup.store';
import { resolveLinkedEventDisplayLabel } from '../../utils/resolve-linked-event-display-label.util';
import { QuickSignDocumentDetailsSummary } from '../quick-sign-document-details-summary';

export const SignatureDocumentDetailsModal: FC = () => {
  const { messagesLocale } = useLocale();
  const isOpen = SignaturePopupStore(({ open }) => open);
  const type = SignaturePopupStore(({ type }) => type);
  const metadata = SignaturePopupStore(({ metadata }) => metadata);
  const handleDismiss = SignaturePopupStore(({ handleDismiss }) => handleDismiss);

  const details = useMemo(() => {
    if (!metadata || !('signatories' in metadata) || !('category' in metadata)) {
      return null;
    }
    return metadata as ViewDocumentDetailsMetadata;
  }, [metadata]);

  const signatoryLines = useMemo(() => {
    if (!details) return [];
    return [...(details.signatories ?? [])]
      .sort((left, right) => (left.sortOrder ?? 0) - (right.sortOrder ?? 0))
      .map((signatory, index) => {
        const name = signatory.stakeholder?.fullName;
        if (!name) return null;
        return `${index + 1}. ${name}`;
      })
      .filter((line): line is string => line != null);
  }, [details]);

  const signingMode = useMemo(() => {
    if (!details?.signatories?.length) return SigningMode.ALL_AT_ONCE;
    const hasSigningOrder = details.signatories.some((signatory) => signatory.sortOrder != null);
    return hasSigningOrder ? SigningMode.SET_ORDER : SigningMode.ALL_AT_ONCE;
  }, [details]);

  const { options } = useLinkedTargetOptions(details?.companyId, details?.subCategory ?? undefined);

  const linkedEventLabel = useMemo(() => {
    if (!details) return undefined;
    return resolveLinkedEventDisplayLabel({
      linkedEventLabel: details.linkedEventLabel,
      linkedTargetId: details.linkedTargetId,
      options,
    });
  }, [details, options]);

  if (type !== SignaturePopupType.VIEW_DOCUMENT_DETAILS || !details) {
    return null;
  }

  const expirationPeriod = details.expirationPeriod ?? null;

  return (
    <ModalWrapper
      className="w-full max-w-[485px] p-1"
      control={{ open: isOpen }}
      onBgClick={handleDismiss}
      side="RIGHT"
    >
      <div className="flex h-full w-full flex-col overflow-hidden rounded-lg">
        <div className="flex w-full items-center justify-between bg-white px-6 pt-3">
          <span className="text-xl font-semibold text-gray-700">
            <AppFormattedMessage id="DOCUMENT_DETAILS" />
          </span>
          <CloseModalButton onClose={handleDismiss} />
        </div>
        <div className="flex flex-1 flex-col overflow-hidden bg-gray-100">
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto bg-white px-6 py-4">
            <QuickSignDocumentDetailsSummary
              categoryLabel={
                getDocumentCategoryDisplayLabel(details.category, messagesLocale) ?? '-'
              }
              documentName={details.name}
              expirationPeriodLabel={
                expirationPeriod ? (
                  <AppFormattedMessage id={expirationPeriodLabelKey[expirationPeriod]} />
                ) : undefined
              }
              linkedEventLabel={linkedEventLabel}
              showExpiration={expirationPeriod != null}
              signatoryLines={signatoryLines}
              signingModeLabel={messagesLocale[signingModeLabelKey[signingMode]] ?? '-'}
              subCategoryLabel={getDocumentSubCategoryDisplayLabel(
                details.subCategory,
                messagesLocale,
              )}
            />
          </div>
          <div className="flex justify-end px-6 py-4">
            <Button
              className="bg-brand-700 px-6 py-2 text-sm font-medium text-white"
              onClick={handleDismiss}
              styleType="DEFAULT_ROUNDED"
              type="button"
            >
              <AppFormattedMessage id="CLOSE" />
            </Button>
          </div>
        </div>
      </div>
    </ModalWrapper>
  );
};
